/**
 * Regras de TELA da ordem de serviço — o que mostrar, não o que vale.
 *
 * As transições espelham `work-order-rules.ts` da API só para esconder o botão
 * que seria recusado; quem decide continua sendo o servidor.
 */
import type { MaintenanceCriticalityDto, MaintenanceWorkOrderDto, MaintenanceWorkOrderStatusDto } from "@bi/types";

export interface WorkOrderAction {
  to: MaintenanceWorkOrderStatusDto;
  label: string;
  /** pede texto antes de confirmar: laudo (concluir) ou motivo (cancelar) */
  requires?: "resolution" | "cancelReason";
  tone: "primary" | "secondary" | "danger";
}

const ACOES: Record<MaintenanceWorkOrderStatusDto, WorkOrderAction[]> = {
  open: [
    { to: "in_progress", label: "Iniciar execução", tone: "primary" },
    { to: "scheduled", label: "Agendar", tone: "secondary" },
    { to: "cancelled", label: "Cancelar O.S.", requires: "cancelReason", tone: "danger" },
  ],
  scheduled: [
    { to: "in_progress", label: "Iniciar execução", tone: "primary" },
    { to: "open", label: "Voltar para aberta", tone: "secondary" },
    { to: "cancelled", label: "Cancelar O.S.", requires: "cancelReason", tone: "danger" },
  ],
  in_progress: [
    { to: "completed", label: "Concluir", requires: "resolution", tone: "primary" },
    { to: "waiting_parts", label: "Aguardando peças", tone: "secondary" },
    { to: "cancelled", label: "Cancelar O.S.", requires: "cancelReason", tone: "danger" },
  ],
  waiting_parts: [
    { to: "in_progress", label: "Peças chegaram — retomar", tone: "primary" },
    { to: "cancelled", label: "Cancelar O.S.", requires: "cancelReason", tone: "danger" },
  ],
  completed: [],
  cancelled: [],
};

export function workOrderActions(status: MaintenanceWorkOrderStatusDto): WorkOrderAction[] {
  return ACOES[status];
}

export const isWorkOrderClosed = (s: MaintenanceWorkOrderStatusDto) => s === "completed" || s === "cancelled";

const PRIORIDADE: Record<MaintenanceCriticalityDto, number> = { high: 0, medium: 1, low: 2 };

/**
 * Ordem da fila de trabalho: atrasada primeiro, depois veículo parado, depois
 * prioridade, depois o prazo mais próximo; sem prazo vai para o fim.
 */
export function sortWorkQueue<T extends Pick<MaintenanceWorkOrderDto, "overdue" | "vehicleStopped" | "priority" | "dueAt" | "number">>(rows: T[]): T[] {
  return [...rows].sort(
    (a, b) =>
      Number(!!b.overdue) - Number(!!a.overdue) ||
      Number(b.vehicleStopped) - Number(a.vehicleStopped) ||
      PRIORIDADE[a.priority] - PRIORIDADE[b.priority] ||
      (a.dueAt ? new Date(a.dueAt).getTime() : Infinity) - (b.dueAt ? new Date(b.dueAt).getTime() : Infinity) ||
      a.number - b.number,
  );
}

/** "2026-09-30T00:00:00.000Z" → "2026-09-30" para `<input type="date">`, no fuso de SP. */
export function toDateInput(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });
}

/** "2026-09-30" do `<input type="date">` → fim do dia em SP (prazo vale o dia inteiro). */
export function fromDateInput(v: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return null;
  return new Date(`${v}T23:59:59-03:00`).toISOString();
}

/**
 * Número digitado por gente: "1.234,56", "45,90", "45.90" e "1234" valem o que
 * parecem. Com vírgula, ela é o decimal e os pontos são milhar; sem vírgula, um
 * ponto seguido de 1–2 dígitos no fim é decimal ("45.90"), senão é milhar ("1.234").
 */
export function parseBRNumber(v: string): number | null {
  const s = v.trim().replace(/\s|R\$/g, "");
  if (s === "") return null;
  let norm: string;
  if (s.includes(",")) norm = s.replace(/\./g, "").replace(",", ".");
  else if (/^\d+\.\d{1,2}$/.test(s)) norm = s;
  else norm = s.replace(/\./g, "");
  const n = Number(norm);
  return Number.isFinite(n) ? n : null;
}
